// Site detail with tabs: Details (the site info), Lifts (every lift at the site with
// its compliance light), Equipment (the site's care-equipment assets with their next
// service + examination status, retire/reactivate and the delete-rule controls --
// Session ACR; the tab appears only when the site has equipment), and Service History
// (a unified, paginated, most-recent-first feed of every visit across all the site's
// assets, colour-coded by job type, with lift / job-type / date filters and expandable
// rows). Office/admin.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, ApiError } from "../../lib/api";
import type { ServiceHistoryItem, SiteDetail } from "../../lib/api";
import { assets as assetsResource, sites as sitesResource } from "../../lib/resources";
import type { Asset } from "../../lib/resources";
import { jobTypeLabel } from "../../lib/jobType";
import JobTypeBadge from "../../components/JobTypeBadge";
import { JobPhotoGallery } from "./JobPhotoGallery";
import { ServiceRecordInline, DiscardChangesDialog } from "./ServiceRecordPanel";
import { SitePhotos } from "./SitePhotos";
import { RaiseWorkMenu } from "./RaiseWorkMenu";
import { SiteDocumentsCard } from "./portalDocumentsUi";
import { ContactsPanel } from "./ContactsPanel";
import { EleReservationLine } from "../../components/EleReservationLine";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { ConfirmButton } from "../../components/ConfirmButton";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { DefaultEngineerCard } from "../../components/DefaultEngineerCard";
import { RfidTagButton } from "./register/RfidTagButton";
import { trainingTypeLabel } from "../../lib/trainingTypes";
import type { TrainingSessionListItemDto } from "../../lib/trainingTypes";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";
import { useAuth } from "../../lib/auth";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { Button, ButtonLink } from "../../components/Button";
import { RecordHeader } from "../../components/RecordHeader";
import { Loader } from "../../components/Loader";
import { EmptyState } from "../../components/EmptyState";
import { StatusPill } from "../../components/StatusPill";
import { formatDate } from "../../lib/format";

type Tab = "details" | "lifts" | "equipment" | "history";

const STATUS_LABEL: Record<string, string> = { potential: "Potential", active: "Active", dead: "Dead" };
// Mirrors ContractDetailPage.tsx's own TIER_LABEL: a display label only, not a derivation (RULE 6
// does not apply to a fixed three-value display map).
const TIER_LABEL: Record<string, string> = { core: "Core", care: "Care", complete: "Complete" };
// A lift's compliance band as the shared pill (the registry's liftCompliance domain), or "Unknown".
function ComplianceWord({ status }: { status: string | null | undefined }) {
  return status ? <StatusPill domain="liftCompliance" status={status} /> : <span className="text-ele-body opacity-60">Unknown</span>;
}

// Built from the shared label helper so the dropdown matches the table cells (e.g.
// "LOLER inspection"); the option values are the engine keys the backend filters on.
const JOB_TYPE_FILTERS = [
  { value: "", label: "All types" },
  // Session ALW adds the two observational types. Labels still come from the ONE shared helper, so
  // the dropdown and the table cells cannot disagree about what a type is called.
  // Session ANG adds the tech visit. The SERVER needs nothing: site.routes.ts validates the filter
  // against Object.values(JobType), so it already accepts a value this dropdown had no way to send.
  ...(["service", "loler", "callout", "repair", "condition_report", "site_inspection", "tech_visit", "insurance_check", "safed_test"] as const).map((value) => ({
    value,
    label: jobTypeLabel(value),
  })),
];

function eleRef(a: Asset): string {
  return a.eleNumber ?? a.legacyEleNumber ?? "Not allocated";
}

// The shared date, or the words "No date" for a missing one (the helper itself prints "-").
function dateOrNoDate(iso: string | null | undefined): string {
  return iso ? formatDate(iso) : "No date";
}

export function SiteDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "office";
  const [tab, setTab] = useState<Tab>("details");

  const [site, setSite] = useState<SiteDetail | null>(null);
  const [siteError, setSiteError] = useState(false);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [liftsLoaded, setLiftsLoaded] = useState(false);

  const loadAssets = useCallback(() => {
    assetsResource
      .list({ siteId: id })
      .then((a) => {
        setAssets(a);
        setLiftsLoaded(true);
      })
      .catch(() => setLiftsLoaded(true));
  }, [id]);

  useEffect(() => {
    api.sites.get(id).then(setSite, () => setSiteError(true));
    loadAssets();
  }, [id, loadAssets]);

  // Session ACR: equipment assets (ELE-NNN-ENN) are their own tab; the Lifts tab stays lifts
  // only. A lift-only site shows no Equipment tab at all (nothing changes for it).
  const lifts = useMemo(() => assets.filter((a) => (a.assetClass ?? "lift") === "lift"), [assets]);
  const equipment = useMemo(() => assets.filter((a) => (a.assetClass ?? "lift") !== "lift"), [assets]);

  // ---- Edit the site's own core fields (Session AQW, item C-9) ----
  // Everything PATCH /api/sites/:id accepts except accessDetails, which keeps its own dedicated
  // inline editor in the Site access panel above (AccessDetailsPanel) -- editing it here too would
  // give the office two different controls for the same field. customerId (reassigning the site to
  // a different customer) is deliberately left to the Sites register's existing inline edit rather
  // than duplicated here: it is a rarer, more structural move than tidying this page calls for, and
  // it would need its own customer-list fetch. See the session report for the full reasoning.
  const [editOpen, setEditOpen] = useState(false);
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  function openEdit() {
    setEditError(null);
    setEditOpen(true);
  }

  async function saveEdit(body: { name: string; address: string; postcode: string; status: string; notes: string; aliases: string[] }) {
    setEditBusy(true);
    setEditError(null);
    try {
      const updated = await sitesResource.update(id, body);
      setSite((prev) => (prev ? { ...prev, ...updated } : prev));
      setEditOpen(false);
    } catch (err) {
      setEditError(friendlyApiError(err, "Could not save the site. Please try again."));
    } finally {
      setEditBusy(false);
    }
  }

  // ---- Delete (admin/office; the server blocks 409 while the site has projects) ----
  // Reuses the office's existing cascade delete, DELETE /api/sites/:id (Session AI, Item 5): the
  // same route the Sites register's own inline delete already calls, and the same MCP tool
  // (delete_site, Session AHF) already dispatches. This page previously had no delete control at
  // all, only the register's.
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function confirmDeleteSite() {
    setDeleting(true);
    setDeleteError(null);
    try {
      await sitesResource.remove(id);
      navigate("/office/sites");
    } catch (err) {
      setDeleteError(friendlyApiError(err, "Could not delete the site. Please try again."));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div>
      <RecordHeader
        className="mb-4"
        backTo="/office/sites"
        backLabel="Back to sites"
        title={site?.name ?? (siteError ? "Site not found" : "Site")}
        subtitle={
          site ? (
            site.customer ? (
              <Link to={`/office/customers/${encodeURIComponent(site.customer.id)}`} className="text-ele-green underline-offset-2 hover:underline">
                {site.customer.name}
              </Link>
            ) : (
              "No customer"
            )
          ) : undefined
        }
        actions={
          site && canEdit ? (
            <>
              {/* Session ASM (W-12): raise work at THIS site through the ordinary flows, pre-filled. With exactly one
                lift in service here, that lift is chosen too; otherwise the office picks it in the flow itself. A site
                with no customer has nobody to raise work for. */}
            {site.customer && (
              <RaiseWorkMenu
                options={["callout", "repair", "safed", "quote"]}
                context={{
                  customerId: site.customer.id,
                  customerName: site.customer.name,
                  siteId: site.id,
                  siteName: site.name,
                  siteAddress: [site.address, site.postcode].filter(Boolean).join(", "),
                  assetId: (() => {
                    const live = assets.filter((a) => !a.retiredAt);
                    return live.length === 1 ? live[0]!.id : null;
                  })(),
                }}
              />
            )}
              <Button variant="secondary" size="sm" onClick={openEdit}>
                Edit
              </Button>
              <Button variant="destructive" size="sm" onClick={() => { setDeleteError(null); setShowDelete(true); }}>
                Delete
              </Button>
            </>
          ) : undefined
        }
      />

      {/* Session AFR: the site's access details, prominent at the TOP so the office sees them the
          moment they open a site record (before any tab content), with an inline quick-edit. Only
          renders when something is recorded; internal-only, never a customer-facing surface. */}
      {site?.accessDetails && (
        <AccessDetailsPanel site={site} onSaved={(v) => setSite((s) => (s ? { ...s, accessDetails: v } : s))} />
      )}

      {/* Session ACG locked tab treatment: quiet text tabs, full-purple underline on the
          active tab only (.ele-tabs / .ele-tab in index.css). */}
      <div className="ele-tabs mb-5">
        {([
          { key: "details", label: "Details" },
          { key: "lifts", label: `Lifts${liftsLoaded ? ` (${lifts.length})` : ""}` },
          ...(equipment.length > 0 ? [{ key: "equipment" as Tab, label: `Equipment (${equipment.length})` }] : []),
          { key: "history", label: "Service history" },
        ] as { key: Tab; label: string }[]).map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} aria-pressed={tab === t.key} className={`ele-tab ${tab === t.key ? "is-active" : ""}`}>
            {t.label}
          </button>
        ))}
      </div>

      {siteError ? (
        <p role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">
          Could not load this site.
        </p>
      ) : tab === "details" ? (
        <DetailsTab
          site={site}
          siteId={id}
          canEdit={canEdit}
          onSiteChanged={(patch) => setSite((s) => (s ? { ...s, ...patch } : s))}
        />
      ) : tab === "lifts" ? (
        <LiftsTab lifts={lifts} loaded={liftsLoaded} />
      ) : tab === "equipment" ? (
        <EquipmentTab equipment={equipment} onChanged={loadAssets} />
      ) : (
        <HistoryTab siteId={id} lifts={assets} />
      )}

      {site && (
        <SiteEditModal
          open={editOpen}
          site={site}
          busy={editBusy}
          error={editError}
          onClose={() => {
            if (editBusy) return;
            setEditOpen(false);
            setEditError(null);
          }}
          onSave={saveEdit}
        />
      )}

      {site && (
        <ConfirmDeleteModal
          open={showDelete}
          title={`Delete ${site.name}?`}
          message="This permanently deletes the site and everything attached to it: its lifts and equipment, contracts, quotes, jobs, service records and defects. Invoices are kept in Xero. This cannot be undone."
          warning={site.contract ? "This site has an active contract on file." : undefined}
          confirmLabel="Delete site"
          busy={deleting}
          error={deleteError}
          onConfirm={confirmDeleteSite}
          onCancel={() => {
            if (deleting) return;
            setShowDelete(false);
            setDeleteError(null);
          }}
        />
      )}
    </div>
  );
}

// The site's own core-fields edit (Session AQW, item C-9): name, address, postcode, status and
// notes -- the PATCH /api/sites/:id fields that are not already edited elsewhere on this page
// (accessDetails keeps AccessDetailsPanel's own inline editor above; customerId reassignment stays
// on the Sites register, see the comment on SiteDetailPage). Follows the EditTrainingSessionModal
// re-seed-on-open convention: local field state resets from the record in a useEffect keyed on
// [open, site], so a cancelled edit never leaks into the next opening. Status is included even
// though the task that named this modal's fields did not: it is schema-accepted, already shown on
// this page, and it is the office's way out of a 409 blocked delete (the delete-check route's own
// wording: "Setting it to dead keeps them and takes it out of use").
function SiteEditModal({
  open,
  site,
  busy,
  error,
  onClose,
  onSave,
}: {
  open: boolean;
  site: SiteDetail;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (body: { name: string; address: string; postcode: string; status: string; notes: string; aliases: string[] }) => void;
}) {
  const [name, setName] = useState(site.name);
  const [address, setAddress] = useState(site.address);
  const [postcode, setPostcode] = useState(site.postcode ?? "");
  const [status, setStatus] = useState(site.status);
  const [notes, setNotes] = useState(site.notes ?? "");
  // Session ATZ (Item 3): one name per line. The server trims, drops duplicates and the site's own name.
  const [aliases, setAliases] = useState((site.aliases ?? []).join("\n"));

  // Re-seed every time the modal opens, so a cancelled edit never leaks into the next one and the
  // form always starts from what is actually on file.
  useEffect(() => {
    if (!open) return;
    setName(site.name);
    setAddress(site.address);
    setPostcode(site.postcode ?? "");
    setStatus(site.status);
    setNotes(site.notes ?? "");
    setAliases((site.aliases ?? []).join("\n"));
  }, [open, site]);

  if (!open) return null;

  const canSave = name.trim().length > 0 && address.trim().length > 0 && !busy;

  function submit() {
    if (!canSave) return;
    onSave({
      name: name.trim(),
      address: address.trim(),
      postcode: postcode.trim(),
      status,
      notes: notes.trim(),
      aliases: aliases.split("\n").map((a) => a.trim()).filter(Boolean),
    });
  }

  return (
    <ModalShell ariaLabel="Edit site" onClose={() => !busy && onClose()} width="max-w-lg" panelClassName="p-5">
      <h2 className="font-heading text-xl text-ele-strong">Edit site</h2>
      <div className="mt-4 space-y-3">
        <label className="block">
          <span className={editLabelCls}>Site name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} className={editInputCls} />
        </label>
        <label className="block">
          <span className={editLabelCls}>Site address</span>
          <input value={address} onChange={(e) => setAddress(e.target.value)} className={editInputCls} />
        </label>
        <label className="block">
          <span className={editLabelCls}>Postcode</span>
          <input value={postcode} onChange={(e) => setPostcode(e.target.value)} className={editInputCls} />
        </label>
        <label className="block">
          <span className={editLabelCls}>Status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className={editInputCls}>
            {Object.entries(STATUS_LABEL).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className={editLabelCls}>Notes</span>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className={editInputCls} />
        </label>
        <label className="block">
          <span className={editLabelCls}>Other names for this site</span>
          <textarea
            value={aliases}
            onChange={(e) => setAliases(e.target.value)}
            rows={2}
            placeholder={"One per line, e.g. Lighterage Court (Kew Reach)"}
            className={editInputCls}
          />
          <span className="mt-1 block text-xs text-ele-body">
            The name a managing agent&rsquo;s platform (Dwellant) uses for this building, so its work orders find this site by themselves.
          </span>
        </label>
      </div>
      {error && (
        <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {error}
        </p>
      )}
      <div className={`mt-5 gap-2 ${MODAL_ACTIONS}`}>
        <button type="button" onClick={onClose} disabled={busy} className={`${editGhostBtn} ${MODAL_ACTION_BTN}`}>
          Cancel
        </button>
        <Button size="sm" onClick={submit} disabled={!canSave} className={MODAL_ACTION_BTN}>
          {busy ? "Saving..." : "Save changes"}
        </Button>
      </div>
    </ModalShell>
  );
}

const editInputCls =
  "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green";
const editLabelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";
const editGhostBtn =
  "rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green disabled:opacity-50";

// The site's access details (Session AFR): door codes, fob/key location, parking, motor-room
// access method, building hours. Shown prominently at the TOP of the site record in the locked
// functional-amber panel (operationally important, not a fault; the cream background carries in
// either theme), with an inline quick-edit so the office corrects it without opening the whole
// site edit form on the register. Internal-only: this field is never on a customer-facing surface.
// Only renders when something is recorded (the caller guards on site.accessDetails); saving a
// blank clears it (optionalText -> null server-side), which unmounts the panel.
function AccessDetailsPanel({ site, onSaved }: { site: SiteDetail; onSaved: (value: string | null) => void }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(site.accessDetails ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function startEdit() {
    setValue(site.accessDetails ?? "");
    setError(null);
    setEditing(true);
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      // Use the server's own persisted value: optionalText trims it and turns a blank into null,
      // so the panel shows exactly what was stored (no whitespace drift) and unmounts when cleared.
      const updated = await sitesResource.update(site.id, { accessDetails: value });
      onSaved(updated.accessDetails ?? null);
      setEditing(false);
    } catch {
      setError("Could not save the access details. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="mb-5 rounded-ele border border-ele-amber-line bg-ele-amber-wash p-4">
      <div className="flex items-start justify-between gap-3">
        <h2 className="font-menu text-xs uppercase tracking-wider text-ele-amber-line">Site access</h2>
        {!editing && (
          <button
            type="button"
            onClick={startEdit}
            className="font-menu text-xs tracking-wide text-ele-amber-line underline underline-offset-2 hover:brightness-125"
          >
            Edit
          </button>
        )}
      </div>
      {editing ? (
        <div className="mt-2">
          <textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            rows={4}
            placeholder="Door codes, fob access, parking, key location, building hours, motor room access..."
            className="w-full rounded-ele border border-ele-amber-line bg-white px-3 py-2 text-sm text-ele-wash-ink outline-none placeholder:text-ele-amber-wash-ink placeholder:opacity-70"
          />
          {error && (
            <p role="alert" className="mt-1 text-xs text-ele-danger">
              {error}
            </p>
          )}
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="rounded-ele bg-ele-amber-line px-3 py-1.5 font-menu text-xs tracking-wide text-ele-bg disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save"}
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              disabled={saving}
              className="rounded-ele border border-ele-amber-line px-3 py-1.5 font-menu text-xs tracking-wide text-ele-amber-line disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <p className="mt-1 whitespace-pre-wrap text-sm text-ele-amber-wash-ink">{site.accessDetails}</p>
      )}
    </section>
  );
}

function DetailsTab({
  site,
  siteId,
  canEdit,
  onSiteChanged,
}: {
  site: SiteDetail | null;
  siteId: string;
  canEdit: boolean;
  onSiteChanged: (patch: Partial<SiteDetail>) => void;
}) {
  if (!site) return <Loader shape="panel" />;
  const rows: [string, React.ReactNode][] = [
    [
      "Customer",
      site.customer ? (
        <Link to={`/office/customers/${encodeURIComponent(site.customer.id)}`} className="text-ele-green underline-offset-2 hover:underline">
          {site.customer.name}
        </Link>
      ) : (
        "None"
      ),
    ],
    ["Address", site.address || "None"],
    ["Postcode", site.postcode || "None"],
    ["Status", STATUS_LABEL[site.status] ?? site.status],
    [
      "Contract",
      site.contract ? (
        <Link to={`/office/contracts/${encodeURIComponent(site.contract.id)}`} className="text-ele-green underline-offset-2 hover:underline">
          {TIER_LABEL[site.contract.tier] ?? site.contract.tier}
          {site.contract.renewalDate ? ` · renews ${dateOrNoDate(site.contract.renewalDate)}` : ""}
        </Link>
      ) : (
        "No active contract"
      ),
    ],
    // Access details are shown prominently in the amber Site-access panel at the top of the page
    // (Session AFR), not buried in this key-value list.
    ["Notes", site.notes || "None"],
  ];
  return (
    <div className="space-y-5">
      <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
        <dl className="grid gap-3 sm:grid-cols-2">
          {rows.map(([k, v]) => (
            <div key={k}>
              <dt className="text-xs tracking-wide text-ele-blue opacity-70">{k}</dt>
              <dd className="mt-0.5 text-sm text-ele-strong">{v}</dd>
            </div>
          ))}
        </dl>
      </div>
      {/* Session ASN (Phase 9, D86): the named people at this building (on-site / facilities, managing agent,
          keyholder), tied to this site. A record only: no automated email reads it. */}
      <EleReservationLine siteId={siteId} />
      {site.customer && <ContactsPanel customerId={site.customer.id} siteId={siteId} />}
      {/* Session ARA (S-4): built to D84's standing RECOMMENDATION, not a confirmed Director
          ruling -- see docs/ELE-V2-DIRECTOR-DECISIONS-2026-07-19.md. Stamped onto every PPM visit
          auto-generated for this site (and an immediate "Start a service now" visit), unless a
          contract at this site sets its own override. */}
      <DefaultEngineerCard
        value={site.defaultEngineerId ?? null}
        canEdit={canEdit}
        description="Stamped onto every PPM visit auto-generated for this site, and onto an immediate 'Start a service now' visit, unless a contract at this site sets its own override. Changeable per visit afterwards like any other assignment."
        onSave={async (defaultEngineerId) => {
          const updated = await sitesResource.update(siteId, { defaultEngineerId });
          onSiteChanged({ defaultEngineerId: updated.defaultEngineerId ?? null });
        }}
      />
      {/* Session ADI: reach the site's LOLER examinations and its defects. These had no surface on
          the site page at all (the audit's "Site -> its LOLER" / "Site -> its defects" gap); each
          link opens the full list filtered to this site (?siteId=). */}
      <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
        <h2 className="mb-3 font-heading text-lg text-ele-blue">Compliance</h2>
        <div className="flex flex-wrap gap-2">
          <Link
            to={`/office/loler?siteId=${encodeURIComponent(siteId)}`}
            className="rounded-ele border border-ele-purple-accent px-3 py-1 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green"
          >
            LOLER examinations
          </Link>
          <Link
            to={`/office/defects?siteId=${encodeURIComponent(siteId)}`}
            className="rounded-ele border border-ele-purple-accent px-3 py-1 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green"
          >
            Defects
          </Link>
        </div>
      </div>
      {/* Standing SITE photos (Session ADE): the site's own reference images, a separate bucket
          from the per-visit photos shown in the Service history tab. */}
      <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
        <h2 className="mb-3 font-heading text-lg text-ele-blue">Site photos</h2>
        <SitePhotos siteId={siteId} />
      </div>
      {/* Site documents (Session AFE): site-specific RAMS / risk assessments shown to this
          site's customer in the Client Portal. Company-wide documents live on the Company
          documents page under Compliance. */}
      <SiteDocumentsCard siteId={siteId} />
      {/* Training (Session AFL): the on-site training ELE has delivered to this site's staff.
          The card shows the completed count and the most recent session; the full list and the
          creation flow live on the Training page, filtered to this site. */}
      <SiteTrainingCard siteId={siteId} />
    </div>
  );
}

// The site's training summary. Self-fetching (the SiteDocumentsCard pattern): one page-1 request
// for the completed count plus the most recent completed session, so the card states a real
// number rather than implying "none" from a failed load.
function SiteTrainingCard({ siteId }: { siteId: string }) {
  const [state, setState] = useState<{ total: number; latest: TrainingSessionListItemDto | null } | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoaded(false);
    setLoadError(false);
    api.training
      .list({ siteId, status: "COMPLETED", pageSize: 1 })
      .then((r) => {
        if (!alive) return;
        setState({ total: r.total, latest: r.items[0] ?? null });
        setLoaded(true);
      })
      .catch(() => {
        if (!alive) return;
        setLoaded(true);
        setLoadError(true);
      });
    return () => {
      alive = false;
    };
  }, [siteId]);

  return (
    <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
      <h2 className="mb-3 font-heading text-lg text-ele-blue">Training</h2>
      {!loaded ? (
        <Loader shape="inline" />
      ) : loadError ? (
        <p role="alert" className="text-sm text-ele-strong">
          Could not load the training records. Refresh to try again.
        </p>
      ) : (
        <p className="text-sm text-ele-body">
          {state && state.total > 0 ? (
            <>
              <span className="text-ele-strong">
                {state.total} completed session{state.total === 1 ? "" : "s"}
              </span>
              {state.latest && (
                <>
                  {". "}
                  Most recent: {trainingTypeLabel(state.latest.trainingType, state.latest.customLabel)} on{" "}
                  {formatDate(state.latest.sessionDate)}.
                </>
              )}
            </>
          ) : (
            "No completed training recorded for this site yet."
          )}
        </p>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        <Link
          to={`/office/training?siteId=${encodeURIComponent(siteId)}`}
          className="rounded-ele border border-ele-purple-accent px-3 py-1 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green max-sm:min-h-11 max-sm:inline-flex max-sm:items-center"
        >
          View all
        </Link>
        <ButtonLink to={`/office/training?siteId=${encodeURIComponent(siteId)}&new=1`} size="sm">
          Log new session
        </ButtonLink>
      </div>
    </div>
  );
}

function LiftsTab({ lifts, loaded }: { lifts: Asset[]; loaded: boolean }) {
  const navigate = useNavigate();
  // Card list below md, table otherwise -- decided once at mount (the mobileLayout mechanism
  // already proven on ~13 other office lists). jsdom has no matchMedia, so every existing test
  // still renders the table it pins.
  const [mobileCards] = useState(initialMobileLayout);
  if (!loaded) return <Loader />;
  if (lifts.length === 0) return <EmptyState message="No lifts at this site yet." />;
  return mobileCards ? (
    /* Mobile card list: the desktop table's three mobile survivors (ELE number, Next service,
       Compliance) still divided a phone-width strip between them, truncating the ELE number --
       the same defect class fixed on the Lifts and equipment register. The card stacks instead,
       and brings back Type and LOLER due (both desktop-only before this) into the details block. */
    <MobileCardList label="Lifts">
      {lifts.map((a) => {
        return (
          <MobileListCard
            key={a.id}
            to={`/office/lifts/${encodeURIComponent(a.id)}`}
            primary={eleRef(a)}
            secondary={a.liftType?.trim() || "Lift"}
            meta={`Next service ${dateOrNoDate(a.nextScheduledServiceAt ?? null)}`}
            right={<ComplianceWord status={a.complianceStatus} />}
            details={
              <p className="text-sm text-ele-body">
                <span className="text-ele-blue">LOLER due:</span> {dateOrNoDate(a.lolerDue ?? null)}
              </p>
            }
          />
        );
      })}
    </MobileCardList>
  ) : (
    // Session ACW (mobile usability): Type and LOLER due hide below md, keeping the identifier
    // (ELE number), the one key date (Next service) and the status (Compliance) -- exactly the
    // three things worth a glance on a phone. The overflow-x-auto wrapper stays as a safety net,
    // but the remaining columns fit without it needing to scroll in practice.
    <div data-safetynet className="overflow-x-auto rounded-ele border border-ele-purple-accent">
      <table className="w-full text-left text-sm">
        <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
          <tr>
            <th className="px-3 py-2">ELE number</th>
            <th className="px-3 py-2 max-md:hidden">Type</th>
            <th className="px-3 py-2">Next service</th>
            <th className="px-3 py-2 max-md:hidden">LOLER due</th>
            <th className="px-3 py-2">Compliance</th>
          </tr>
        </thead>
        <tbody>
          {lifts.map((a) => {
            return (
              // Session ADI: the whole row opens the lift (the Site<->Lift forward click-through the
              // ADG audit flagged). The ELE cell is also a real Link so keyboard focus and
              // open-in-new-tab work; the row onClick ignores clicks that land on it.
              <tr
                key={a.id}
                className="group cursor-pointer border-t border-ele-purple-accent transition hover:bg-ele-row-hover"
                onClick={(e) => {
                  if ((e.target as HTMLElement).closest("a,button,input,select,textarea,label")) return;
                  navigate(`/office/lifts/${encodeURIComponent(a.id)}`);
                }}
              >
                <td className="px-3 py-2 font-heading">
                  <Link to={`/office/lifts/${encodeURIComponent(a.id)}`} className="text-ele-strong underline-offset-2 hover:text-ele-green hover:underline">
                    {eleRef(a)}
                  </Link>
                </td>
                <td className="px-3 py-2 text-ele-body max-md:hidden">{a.liftType?.trim() || "Lift"}</td>
                {/* STALE-DATA RULE (Session AF): show the LIVE projected next scheduled service, never
                    the stored Asset.nextServiceDue, which can be stale once PPM re-projects. Matches the
                    Equipment tab and the Lifts register (Session AEH, FIX-1 A5). */}
                <td className="px-3 py-2">{dateOrNoDate(a.nextScheduledServiceAt ?? null)}</td>
                <td className="px-3 py-2 max-md:hidden">{dateOrNoDate(a.lolerDue ?? null)}</td>
                <td className="px-3 py-2"><ComplianceWord status={a.complianceStatus} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// The equipment cadence, in the customer-facing wording (yearly / six-monthly / quarterly;
// the defensive extras cover a hand-edited payload's bi-monthly/monthly).
const EQUIPMENT_FREQ: Record<string, string> = {
  annual: "Yearly",
  six_monthly: "Six-monthly",
  quarterly: "Quarterly",
  bi_monthly: "Every 2 months",
  monthly: "Monthly",
};

// Session ACR: the site's care-equipment assets. Next service comes from the same enriched
// projection as a lift; the examination column is HONEST -- a people-lifting item with no
// recorded exam reads amber "Awaiting first examination record" (never a fabricated date;
// it also sits on the Compliance page chase list). Retire (reversible) and Delete (two-step;
// refused with a clear message once completed history exists) satisfy the delete rule.
function EquipmentTab({ equipment, onChanged }: { equipment: Asset[]; onChanged: () => void }) {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [deleteFor, setDeleteFor] = useState<Asset | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Card list below md, table otherwise -- decided once at mount (the mobileLayout mechanism
  // already proven on ~13 other office lists). jsdom has no matchMedia, so every existing test
  // still renders the table it pins.
  const [mobileCards] = useState(initialMobileLayout);

  async function retire(a: Asset) {
    setBusyId(a.id);
    setNotice(null);
    try {
      await api.assets.retire(a.id);
      setNotice(`${eleRef(a)} retired. Its history is kept and its forward visits are cleared; Reactivate brings it back.`);
      onChanged();
    } catch {
      setNotice("Could not retire this equipment. Please try again.");
    } finally {
      setBusyId(null);
    }
  }

  async function reactivate(a: Asset) {
    setBusyId(a.id);
    setNotice(null);
    try {
      await api.assets.reactivate(a.id);
      setNotice(`${eleRef(a)} reactivated. Run Generate PPM (or wait for the daily run) to rebuild its forward visits.`);
      onChanged();
    } catch {
      setNotice("Could not reactivate this equipment. Please try again.");
    } finally {
      setBusyId(null);
    }
  }

  async function doDelete() {
    if (!deleteFor) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await assetsResource.remove(deleteFor.id);
      setNotice(`${eleRef(deleteFor)} deleted.`);
      setDeleteFor(null);
      onChanged();
    } catch (e) {
      setDeleteError(e instanceof ApiError ? e.message || e.code : "Could not delete this equipment. Please try again.");
    } finally {
      setDeleteBusy(false);
    }
  }

  // Shared by the desktop table's Actions cell and the mobile card's actions row, so the two
  // can never drift (Retire/Reactivate + Delete is the same control set either way).
  function renderRowActions(a: Asset) {
    const retired = Boolean(a.retiredAt);
    return (
      <>
        {retired ? (
          <button
            type="button"
            onClick={() => reactivate(a)}
            disabled={busyId === a.id}
            className="text-xs text-ele-green underline hover:brightness-110 disabled:opacity-50"
          >
            Reactivate
          </button>
        ) : (
          <ConfirmButton
            onConfirm={() => retire(a)}
            label="Retire"
            confirmLabel="Retire?"
            busy={busyId === a.id}
            busyLabel="Retiring..."
            title="Keeps every record; clears the unstarted forward visits. Reversible."
            className="text-xs text-ele-body underline hover:text-ele-strong disabled:opacity-50"
          />
        )}
        <button
          type="button"
          onClick={() => {
            setDeleteError(null);
            setDeleteFor(a);
          }}
          className="max-sm:min-h-11 max-sm:inline-flex max-sm:items-center text-xs text-ele-body underline hover:text-ele-danger"
        >
          Delete
        </button>
      </>
    );
  }

  if (equipment.length === 0) return <EmptyState message="No care equipment at this site." />;

  return (
    <div>
      {notice && (
        <p role="status" className="mb-3 rounded-ele border border-ele-green bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {notice}
        </p>
      )}
      {mobileCards ? (
        /* Mobile card list: Reference, Next service, Status, RFID tag and Actions were FIVE
           columns still dividing a phone-width strip (no detail page to fall back on, so every
           one of them had to stay visible below md, unlike the Lifts tab). The card stacks
           instead, and brings back Equipment/Serviced/Examination (desktop-only before this)
           into the details block. */
        <MobileCardList label="Equipment">
          {equipment.map((a) => {
            const retired = Boolean(a.retiredAt);
            return (
              <MobileListCard
                key={a.id}
                primary={eleRef(a)}
                secondary={a.liftType?.trim() || "Equipment"}
                meta={`Next service ${retired ? "Not scheduled" : dateOrNoDate(a.nextScheduledServiceAt ?? null)}`}
                right={
                  retired ? <StatusPill word="Retired" tone="grey" /> : <ComplianceWord status={a.complianceStatus} />
                }
                details={
                  <div className="space-y-1 text-sm text-ele-body">
                    <p>
                      <span className="text-ele-blue">Serviced:</span>{" "}
                      {a.frequency ? EQUIPMENT_FREQ[a.frequency] ?? a.frequency : "Not set"}
                    </p>
                    <p>
                      <span className="text-ele-blue">Examination:</span>{" "}
                      {a.lolerDue ? (
                        dateOrNoDate(a.lolerDue)
                      ) : a.peopleLifting ? (
                        <span className="text-amber-400">Awaiting first examination record</span>
                      ) : (
                        <span className="text-ele-body opacity-60">Not required</span>
                      )}
                    </p>
                  </div>
                }
                actions={
                  <>
                    {/* Session ACT: equipment carries an RFID tag like a lift. Same button, equipment
                        kind (its URL resolves to /nfc/e/<token>, no auto-dialler line to the engineer). */}
                    <RfidTagButton asset={a} kind="equipment" />
                    {renderRowActions(a)}
                  </>
                }
              />
            );
          })}
        </MobileCardList>
      ) : (
        /* Session ACW (mobile usability): Equipment/Serviced/Examination hide below md, keeping
            the identifier (Reference), the one key date (Next service) and the status column. The
            RFID tag column and the retire/reactivate/delete actions stay visible at EVERY width:
            this row has no detail page of its own, so they are the only way to manage the row and
            its tag (review fix - hiding the tag button had left equipment RFID unmanageable on a
            phone). The overflow-x-auto wrapper stays as a safety net. */
        <div data-safetynet className="overflow-x-auto rounded-ele border border-ele-purple-accent">
          <table className="w-full text-left text-sm">
            <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
              <tr>
                <th className="px-3 py-2">Reference</th>
                <th className="px-3 py-2 max-md:hidden">Equipment</th>
                <th className="px-3 py-2 max-md:hidden">Serviced</th>
                <th className="px-3 py-2">Next service</th>
                <th className="px-3 py-2 max-md:hidden">Examination</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">RFID tag</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {equipment.map((a) => {
                const retired = Boolean(a.retiredAt);
                    return (
                  <tr key={a.id} className={`border-t border-ele-purple-accent ${retired ? "opacity-60" : ""}`}>
                    <td className="px-3 py-2 font-heading text-ele-strong">{eleRef(a)}</td>
                    <td className="px-3 py-2 text-ele-body max-md:hidden">{a.liftType?.trim() || "Equipment"}</td>
                    <td className="px-3 py-2 text-ele-body max-md:hidden">{a.frequency ? EQUIPMENT_FREQ[a.frequency] ?? a.frequency : "Not set"}</td>
                    <td className="px-3 py-2">{retired ? "Not scheduled" : dateOrNoDate(a.nextScheduledServiceAt ?? null)}</td>
                    <td className="px-3 py-2 max-md:hidden">
                      {a.lolerDue ? (
                        dateOrNoDate(a.lolerDue)
                      ) : a.peopleLifting ? (
                        <span className="text-amber-400">Awaiting first examination record</span>
                      ) : (
                        <span className="text-ele-body opacity-60">Not required</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {retired ? <StatusPill word="Retired" tone="grey" /> : <ComplianceWord status={a.complianceStatus} />}
                    </td>
                    <td className="px-3 py-2">
                      {/* Session ACT: equipment carries an RFID tag like a lift. Same button, equipment
                          kind (its URL resolves to /nfc/e/<token>, no auto-dialler line to the engineer). */}
                      <RfidTagButton asset={a} kind="equipment" />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex items-center justify-end gap-3">{renderRowActions(a)}</div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <ConfirmDeleteModal
        open={deleteFor != null}
        title={`Delete ${deleteFor ? eleRef(deleteFor) : "this equipment"}?`}
        message="This permanently removes the equipment asset and its records. Equipment with completed service history cannot be deleted; retire it instead so the record is kept."
        confirmLabel="Delete equipment"
        variant="destructive"
        busy={deleteBusy}
        error={deleteError}
        onConfirm={doDelete}
        onCancel={() => {
          if (deleteBusy) return;
          setDeleteFor(null);
          setDeleteError(null);
        }}
      />
    </div>
  );
}

function HistoryTab({ siteId, lifts }: { siteId: string; lifts: Asset[] }) {
  const [assetId, setAssetId] = useState("");
  const [jobType, setJobType] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  // Session AGZ: card list below md (decided once at mount -- see src/lib/mobileLayout.ts;
  // the inline record panel must only ever mount once, so the layouts are either/or).
  const [mobileCards] = useState(initialMobileLayout);

  const [items, setItems] = useState<ServiceHistoryItem[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  // Session ADF: a completed visit's row opens the shared ServiceRecordPanel INLINE (the same
  // mechanism as the lift detail page), so the office reads and amends the full record without
  // leaving the site. The lite notes/photo peek above stays; this is the one-click route to the
  // full sheet. Only a visit with a sheet on file (hasJobSheet) offers it: a scheduled visit has
  // no record. Collapsing the panel while an inline edit is unsaved asks before discarding.
  const [openRecordId, setOpenRecordId] = useState<string | null>(null);
  const recordDirtyRef = useRef(false);
  const [pendingCollapse, setPendingCollapse] = useState<(() => void) | null>(null);
  const requestRecordCollapse = useCallback((collapse: () => void) => {
    if (recordDirtyRef.current) setPendingCollapse(() => collapse);
    else collapse();
  }, []);
  const toggleRecord = useCallback(
    // Opening another record also collapses the current one, so both paths run the unsaved-edit guard.
    (jobId: string) => requestRecordCollapse(() => setOpenRecordId((cur) => (cur === jobId ? null : jobId))),
    [requestRecordCollapse],
  );
  // Monotonic request token: only the latest fired request may mutate state, so a
  // slow earlier response (a fast filter change, or Load-more then a filter change)
  // can never overwrite or mix into the current filter's rows.
  const reqSeq = useRef(0);

  const params = useMemo(
    () => ({ assetId: assetId || undefined, jobType: jobType || undefined, dateFrom: dateFrom || undefined, dateTo: dateTo || undefined }),
    [assetId, jobType, dateFrom, dateTo],
  );

  const loadPage = useCallback(
    async (nextPage: number, replace: boolean) => {
      const myReq = ++reqSeq.current;
      setLoading(true);
      setError(false);
      try {
        const res = await api.sites.serviceHistory(siteId, { ...params, page: nextPage, limit: 20 });
        if (myReq !== reqSeq.current) return; // a newer request superseded this one
        setItems((cur) => (replace ? res.items : [...cur, ...res.items]));
        setPage(res.page);
        setHasMore(res.hasMore);
        setTotal(res.total);
      } catch {
        if (myReq === reqSeq.current) setError(true);
      } finally {
        if (myReq === reqSeq.current) setLoading(false);
      }
    },
    [siteId, params],
  );

  // Reload from page 1 whenever a filter changes. Collapse any open record panel (its row may be
  // filtered out) and clear the dirty flag, mirroring JobSheetList.load.
  useEffect(() => {
    setOpenRecordId(null);
    recordDirtyRef.current = false;
    void loadPage(1, true);
  }, [loadPage]);

  // The two expansion bodies (visit details peek; the full inline record panel), shared by
  // the desktop table rows and the Session AGZ mobile cards so the layouts cannot drift.
  const detailsBody = (v: ServiceHistoryItem) => (
    <>
      {v.notes && (
        <p className="mb-2 text-sm text-ele-body">
          <span className="text-ele-blue">Notes:</span> {v.notes}
        </p>
      )}
      {v.defects.length > 0 && (
        <div>
          <p className="mb-1 text-xs uppercase tracking-wide text-ele-blue opacity-70">Defects raised</p>
          <ul className="list-inside list-disc text-sm text-ele-strong">
            {v.defects.map((d, i) => (
              <li key={i}>{d}</li>
            ))}
          </ul>
        </div>
      )}
      {v.photoCount > 0 && <JobPhotoGallery jobId={v.id} count={v.photoCount} label="Photos from this visit" />}
    </>
  );
  const recordBody = (v: ServiceHistoryItem) => (
    <ServiceRecordInline
      jobId={v.id}
      onChanged={() => {
        recordDirtyRef.current = false;
        setOpenRecordId(null);
        void loadPage(1, true);
      }}
      onDirtyChange={(d) => {
        recordDirtyRef.current = d;
      }}
    />
  );

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Filter label="Lift">
          <select value={assetId} onChange={(e) => setAssetId(e.target.value)} className="rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1.5 text-sm outline-none focus:border-ele-green">
            <option value="">All lifts</option>
            {lifts.map((a) => (
              <option key={a.id} value={a.id}>
                {eleRef(a)}
              </option>
            ))}
          </select>
        </Filter>
        <Filter label="Type">
          <select value={jobType} onChange={(e) => setJobType(e.target.value)} className="rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1.5 text-sm outline-none focus:border-ele-green">
            {JOB_TYPE_FILTERS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Filter>
        <Filter label="From">
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1.5 text-sm outline-none focus:border-ele-green" />
        </Filter>
        <Filter label="To">
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1.5 text-sm outline-none focus:border-ele-green" />
        </Filter>
        {(assetId || jobType || dateFrom || dateTo) && (
          <button
            onClick={() => {
              setAssetId("");
              setJobType("");
              setDateFrom("");
              setDateTo("");
            }}
            className="py-1.5 text-xs text-ele-body underline hover:text-ele-strong"
          >
            Clear filters
          </button>
        )}
        <span className="ml-auto py-1.5 text-xs text-ele-body opacity-70">{total} visit{total === 1 ? "" : "s"}</span>
      </div>

      {error ? (
        <p role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">
          Could not load the service history. Please try again.
        </p>
      ) : items.length === 0 && !loading ? (
        <EmptyState message={`No visits recorded${assetId || jobType || dateFrom || dateTo ? " for this filter" : " yet"}.`} />
      ) : mobileCards ? (
        /* Session AGZ mobile card list: full ELE reference, date, outcome and the locked
           type colour, with the same Details / Open service record actions as 44px
           targets. Both expansion bodies are the shared helpers above. */
        <ul className="space-y-3">
          {items.map((v) => {
            const canExpand = v.defects.length > 0 || !!v.notes || v.photoCount > 0;
            const open = expanded === v.id;
            const recordOpen = openRecordId === v.id;
            return (
              // NO overflow-hidden (adversarial-review HIGH, mirrors JobSheetList.CardRow):
              // the inline record panel's More menu opens past the card edge and an
              // overflow-hidden wrapper clips it untappable.
              <li key={v.id} className="rounded-ele border border-ele-purple-accent bg-ele-surface">
                <div className="px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <span className="min-w-0 flex-1">
                      <span className="block font-mono text-sm font-semibold text-ele-strong">{v.reference ?? v.eleNumber}</span>
                      <span className="block text-xs text-ele-body opacity-70">{dateOrNoDate(v.dateIso)}</span>
                      <span className="mt-0.5 block break-words text-sm text-ele-body">{v.outcome}</span>
                    </span>
                    <JobTypeBadge type={v.type} size="sm" />
                  </div>
                  {(canExpand || v.hasJobSheet) && (
                    <div className="mt-1 flex flex-wrap items-center gap-4">
                      {canExpand && (
                        <button
                          onClick={() => setExpanded((c) => (c === v.id ? null : v.id))}
                          className="flex min-h-11 items-center text-xs text-ele-body hover:text-ele-strong"
                        >
                          {open ? "Hide" : "Details"}
                        </button>
                      )}
                      {v.hasJobSheet && (
                        <button
                          onClick={() => toggleRecord(v.id)}
                          className="flex min-h-11 items-center text-xs text-ele-green hover:underline"
                        >
                          {recordOpen ? "Hide record" : "Open service record"}
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {open && canExpand && <div className="border-t border-ele-purple-accent px-4 py-3">{detailsBody(v)}</div>}
                {recordOpen && <div className="border-t border-ele-purple-accent px-3 py-4">{recordBody(v)}</div>}
              </li>
            );
          })}
        </ul>
      ) : (
        /* NO overflow-hidden (same review finding): it clipped the last row's expanded
           record-panel menu; the class was only rounding corners. */
        <div className="rounded-ele border border-ele-purple-accent">
          <table className="w-full text-left text-sm">
            <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
              <tr>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Reference</th>
                <th className="max-md:hidden px-3 py-2">Type</th>
                <th className="max-md:hidden px-3 py-2">Engineer</th>
                <th className="px-3 py-2">Outcome</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {items.map((v) => {
                const canExpand = v.defects.length > 0 || !!v.notes || v.photoCount > 0;
                const open = expanded === v.id;
                const recordOpen = openRecordId === v.id;
                return (
                  <FragmentRow key={v.id}>
                    <tr className="border-t border-ele-purple-accent">
                      <td className="px-3 py-2 align-top">{dateOrNoDate(v.dateIso)}</td>
                      <td className="px-3 py-2 align-top font-heading text-ele-strong">{v.reference ?? v.eleNumber}</td>
                      <td className="max-md:hidden px-3 py-2 align-top">
                        <JobTypeBadge type={v.type} size="sm" />
                      </td>
                      <td className="max-md:hidden px-3 py-2 align-top text-ele-body">{v.engineerFirstName ?? "Unassigned"}</td>
                      <td className="px-3 py-2 align-top">{v.outcome}</td>
                      <td className="px-3 py-2 text-right align-top">
                        <div className="flex flex-wrap items-center justify-end gap-3">
                          {canExpand && (
                            <button onClick={() => setExpanded((c) => (c === v.id ? null : v.id))} className="text-xs text-ele-body hover:text-ele-strong">
                              {open ? "Hide" : "Details"}
                            </button>
                          )}
                          {/* Session ADF: the one-click route to the full service-record panel, for a
                              visit that has a sheet on file. A scheduled visit shows no such action. */}
                          {v.hasJobSheet && (
                            <button onClick={() => toggleRecord(v.id)} className="text-xs text-ele-green hover:underline">
                              {recordOpen ? "Hide record" : "Open service record"}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {open && canExpand && (
                      <tr className="border-t border-ele-purple-accent bg-ele-surface">
                        <td colSpan={6} className="px-4 py-3">
                          {detailsBody(v)}
                        </td>
                      </tr>
                    )}
                    {recordOpen && (
                      <tr className="border-t border-ele-purple-accent bg-ele-surface">
                        <td colSpan={6} className="cell-wrap px-4 py-4">
                          {recordBody(v)}
                        </td>
                      </tr>
                    )}
                  </FragmentRow>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 flex justify-center">
        {hasMore && (
          <Button variant="secondary" size="sm" onClick={() => loadPage(page + 1, false)} loading={loading}>
            Load more
          </Button>
        )}
        {loading && items.length === 0 && <Loader shape="inline" />}
      </div>

      <DiscardChangesDialog
        open={pendingCollapse != null}
        onKeep={() => setPendingCollapse(null)}
        onDiscard={() => {
          recordDirtyRef.current = false;
          pendingCollapse?.();
          setPendingCollapse(null);
        }}
      />
    </div>
  );
}

function Filter({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      {/* Session AGZ sentence case: filter labels are form labels, never forced caps. */}
      <span className="text-xs tracking-wide text-ele-body opacity-70">{label}</span>
      {children}
    </label>
  );
}

// React fragment that can take a key in a list (two <tr> per row).
function FragmentRow({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}
